import { useCallback, useEffect, useRef, useState, type ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormStore } from "../store/formStore";
import { useLockStore } from "../store/lockStore";
import type { SaveStatus } from "../types";
import { useStepValidation, useReviewMode } from "../hooks";
import { useLockManager } from "../hooks/useLockManager";
import { useSubmitInFlightRecovery } from "../hooks/useSubmitInFlightRecovery";
import { useAuth } from "../auth/AuthProvider";
import { getApi } from "../api";
import { Header } from "../components/Header";
import { BottomNav } from "../components/BottomNav";
import { DevToolsPanel } from "../components/DevToolsPanel";
import { Spinner } from "../components/Spinner";
import { LockScreen } from "../components/LockScreen";
import { RequestAccessOverlay } from "../components/RequestAccessOverlay";
import { AccessRequestBanner } from "../components/AccessRequestBanner";
import { RemovedScreen } from "../components/RemovedScreen";
import { SubmittingOverlay } from "../components/SubmittingOverlay";
import { useUiStore } from "../store/uiStore";
import { trackPageView, trackWarning } from "../telemetry/appInsights";
import { asError, eventBus } from "@optimyl/events";
import { saveEmployees } from "../services/saveDraft";
import { getConfig } from "../config";

import {
  StepWelcome,
  StepCompany,
  StepContacts,
  StepStructure,
  StepCoverage,
  StepEligibility,
  StepPayment,
  StepDocuments,
  StepReview,
  StepThankYou,
} from "../steps";

/**
 * Map step numbers to their components.
 * Step 0 is the welcome/instructions page shown immediately after login.
 */
const STEP_COMPONENTS: Record<number, ComponentType<{ validation: ReturnType<typeof useStepValidation> }>> = {
  0: StepWelcome,
  1: StepCompany,
  2: StepContacts,
  3: StepStructure,
  4: StepCoverage,
  5: StepEligibility,
  6: StepPayment,
  7: StepDocuments,
  8: StepReview,
};

const SAVE_STATUS_CONFIG: Record<SaveStatus, { label: string; classes: string; dotClass: string } | null> = {
  idle: null,
  saving: { label: "Saving...", classes: "bg-gray-100 text-gray-600", dotClass: "bg-gray-400 animate-pulse" },
  saved: { label: "Saved", classes: "bg-success-bg text-success", dotClass: "bg-success" },
  error: { label: "Save failed", classes: "bg-danger-bg text-danger", dotClass: "bg-danger" },
};

function SavePill({ status }: { status: SaveStatus }) {
  const config = SAVE_STATUS_CONFIG[status];
  if (!config) return null;
  return (
    <div className="absolute top-2 right-6 z-10">
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${config.classes}`} aria-live="polite">
        <span className={`h-1.5 w-1.5 rounded-full ${config.dotClass}`} aria-hidden="true" />
        {config.label}
      </span>
    </div>
  );
}

/**
 * The wizard orchestrator — equivalent of ihqWebsiteMain in Salesforce.
 *
 * Loads form data from the API (mock or real) on mount, then renders
 * Header + current step + BottomNav. Creates a shared validation hook
 * instance and passes it to both the step component and BottomNav.
 */
export function FormPage() {
  const currentStep = useFormStore((s) => s.currentStep);
  const formStatus = useFormStore((s) => s.formStatus);
  const saveStatus = useFormStore((s) => s.saveStatus);
  const isSubmitting = useUiStore((s) => s.isSubmitting);
  const formVersion = useFormStore((s) => s.formVersion);
  const loadFromApi = useFormStore((s) => s.loadFromApi);
  const formId = useFormStore((s) => s.formId);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const hasLoadedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const validation = useStepValidation();

  // Lock state — read from the store; the manager drives transitions via SignalR + heartbeat.
  // Disabled for submitted forms: the thank-you page is read-only, so any number of
  // authorized users can view it concurrently with no contention. This is also what
  // makes the lock layer future-safe for v1/v2 form versioning — external users
  // revisiting a submitted v1 bypass the lock entirely, while internal users editing
  // a v2 corrected record (different recordId, different lock key) are completely
  // independent. See form-locking.md § 6.1.
  //
  // onLockGranted re-fetches form data after a grant transfer so the new holder
  // sees whatever the previous holder saved on their way out. The lock manager
  // awaits this promise before flipping lockStatus to "locked" so the form
  // renders once with the latest values.
  const reloadFormData = useCallback(async () => {
    const api = getApi();
    const proposalId = searchParams.get("oppId");
    const form = proposalId
      ? await api.getIntakeFormByProposal(proposalId)
      : await api.getIntakeForm("mock-form-001");
    if (form) loadFromApi(form);
  }, [searchParams, loadFromApi]);
  // Read-only "review mode" (any submitted form, or an external user viewing a correction)
  // bypasses the lock — there's no editing to contend over. Only an editable draft (an
  // internal user's in-progress correction, or an external user's initial v1) takes the lock.
  const reviewMode = useReviewMode();
  const lockEnabled = !loading && !reviewMode;
  useLockManager(formId, {
    enabled: lockEnabled,
    onLockGranted: reloadFormData,
  });
  // Catch the refresh-during-submit race: a localStorage signal from BottomNav.handleSubmit
  // tells us a submit was in flight when this page last left. Poll SF and flip the UI to
  // submitted (→ Thank-You via reviewMode) once the backend resolves.
  useSubmitInFlightRecovery(formId, loading);
  const lockStatus = useLockStore((s) => s.lockStatus);
  const incomingRequest = useLockStore((s) => s.incomingRequest);

  // Claim the ref synchronously BEFORE any await — StrictMode's second mount
  // must short-circuit before issuing a second POST and creating a duplicate V1.
  // No `cancelled` flag: the first mount's cleanup runs before the second
  // fires, which would nuke the only call that's actually loading data.
  useEffect(() => {
    if (hasLoadedRef.current) return;
    const authEnabled = getConfig().AUTH_ENABLED;
    if (authEnabled && !isAuthenticated) return;
    hasLoadedRef.current = true;

    async function loadForm() {
      try {
        const api = getApi();
        const proposalId = searchParams.get("oppId");

        let form;
        if (proposalId) {
          form = await api.getIntakeFormByProposal(proposalId);
          if (!form) {
            form = await api.createIntakeForm(proposalId);
          }
        } else {
          // No opp ID — fall back to mock form for USE_MOCKS=true UI work.
          form = await api.getIntakeForm("mock-form-001");
        }

        loadFromApi(form);
        setLoading(false);

        // Load-time cleanup: delete orphaned files in parallel via Promise.allSettled
        // so one failure doesn't abort the batch — each rejection is logged individually.
        if (form.staleFileIds.length > 0) {
          void Promise.allSettled(
            form.staleFileIds.map((fileId) =>
              api.deleteFile(fileId).catch((err) => {
                trackWarning("Load cleanup: file delete failed", { fileId, error: String(err) });
              }),
            ),
          );
        }
        // Save modified employees (Claims UW clear, or plan/dental/vision clearing).
        // Routed through saveEmployees() so it shares the same serialization chain
        // as user-driven employee saves — a user editing immediately after load
        // can't have their PUT race with this cleanup PUT.
        if (form.employeesModified) {
          void saveEmployees();
        }
      } catch (err) {
        // Allow a retry — without resetting, the next load would silently skip too.
        hasLoadedRef.current = false;
        eventBus.publish("error", {
          error: asError(err),
          context: "FormPage.loadForm",
        });
        setLoading(false);
      }
    }

    loadForm();
  }, [loadFromApi, isAuthenticated, searchParams]);

  // Track page views when step changes
  useEffect(() => {
    trackPageView(`Intake Step ${currentStep}`);
  }, [currentStep]);

  // Each step starts at the top — covers initial mount (post-login the browser
  // can land mid-page on mobile), the submit transition to the thank-you page,
  // and any nav that doesn't scroll itself.
  useEffect(() => {
    if (loading) return;
    window.scrollTo({ top: 0 });
  }, [currentStep, formStatus, loading]);

  // Spinner stays up until BOTH the form data has loaded AND the initial lock
  // state has resolved. Without the lock-loading guard, the form flashes
  // visibly between "form data loaded" and "acquireLock returned" — most
  // noticeable when reloading as a non-holder, where the form renders briefly
  // before being replaced by the lock screen.
  const lockStillResolving = lockEnabled && lockStatus === "loading";
  if (loading || lockStillResolving) {
    return <Spinner overlay />;
  }

  // Lock-screen overlays: replace the whole form when we don't have edit access.
  // Only shown when the lock is active — read-only review mode and submitted forms
  // skip the lock entirely (no contention possible).
  if (formId && lockEnabled) {
    if (lockStatus === "locked-by-other") {
      return <LockScreen formId={formId} onLockGranted={reloadFormData} />;
    }
    if (lockStatus === "requesting") {
      return <RequestAccessOverlay formId={formId} />;
    }
    if (lockStatus === "removed") {
      return <RemovedScreen formId={formId} onLockGranted={reloadFormData} />;
    }
  }

  // Read-only "review mode" (any submitted form, or an external user viewing a correction)
  // renders the Thank-You page; otherwise the editable wizard. Submitted forms are read-only
  // for everyone — internal users edit only the latest not-yet-submitted version (ADO-3440).
  const StepComponent = reviewMode
    ? StepThankYou
    : (STEP_COMPONENTS[currentStep] ?? StepWelcome);
  const showDevTools = getConfig().SHOW_DEV_TOOLS;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="sticky top-0 z-40">
        {formId && incomingRequest && !reviewMode && <AccessRequestBanner formId={formId} />}
        <Header validation={validation} reviewMode={reviewMode} />
      </div>
      <main id="main-content" className="flex-1 max-w-4xl mx-auto w-full px-6 py-6 relative">
        {!reviewMode && <SavePill status={saveStatus} />}
        <StepComponent validation={validation} />
      </main>
      <BottomNav validation={validation} reviewMode={reviewMode} />
      {showDevTools && <DevToolsPanel />}
      {isSubmitting && <SubmittingOverlay updating={(formVersion ?? 1) >= 2} />}
    </div>
  );
}
